import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ChartDataPointDto } from 'src/core/models/dashboard/chart-data-point.dto';
import { BaseRequestDto } from 'src/core/models/request/base-request.dto';
import { MetricValueResultDto } from 'src/core/models/dashboard/metric-value-result.dto';
import { MetricTrendResultDto } from 'src/core/models/dashboard/metric-trend-result.dto';
import { CsatPercentageResultDto } from 'src/core/models/dashboard/csat-percentage-result.dto';
import { MetricAiHumanTimeSeriesDto } from 'src/core/models/dashboard/metric-ai-human-time-series.dto';
import { SessionTrendResultDto } from 'src/core/models/dashboard/session-trend-result.dto';
import { MetricLastUpdatedDto } from 'src/core/models/dashboard/metric-last-updated.dto';
import { WrapUpDistributionItemDto } from 'src/core/models/dashboard/wrap-up-distribution-item.dto';
import { MetricDefinitionDto } from 'src/core/models/dashboard/metric-definition.dto';
import { MetricIds } from 'src/core/models/enum/metric-ids.enum';
import { API_PREFIX, resolveUrl } from 'src/core/constants/api.constants';
import { METRIC_ENDPOINTS } from '../constants/dashboard-widgets.constants';

const DASHBOARD = `${API_PREFIX}/dashboard`;

@Injectable({ providedIn: 'root' })
export class DashboardService {

  constructor(private http: HttpClient) {}

  // Generic metric methods
  getMetricValue(metricId: MetricIds, request: BaseRequestDto): Observable<MetricValueResultDto> {
    const endpoint = METRIC_ENDPOINTS[metricId]?.value;
    if (!endpoint) {
      return throwError(() => new Error(`Unknown metric ID: ${metricId}`));
    }
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<MetricValueResultDto>(resolveUrl(endpoint), { params })
      .pipe(catchError(error => this.handleError(`getMetricValue[${MetricIds[metricId]}]`, error)));
  }

  getMetricTrend(metricId: MetricIds, request: BaseRequestDto): Observable<MetricTrendResultDto> {
    const endpoint = METRIC_ENDPOINTS[metricId]?.trend;
    if (!endpoint) {
      return throwError(() => new Error(`Unknown metric ID: ${metricId}`));
    }
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<MetricTrendResultDto>(resolveUrl(endpoint), { params })
      .pipe(catchError(error => this.handleError(`getMetricTrend[${MetricIds[metricId]}]`, error)));
  }

  getSessionTrend(request: BaseRequestDto): Observable<SessionTrendResultDto> {
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<SessionTrendResultDto>(resolveUrl(`${DASHBOARD}/session-trend`), { params })
      .pipe(catchError(error => this.handleError('getSessionTrend', error)));
  }

  getWrapUpDistribution(request: BaseRequestDto): Observable<WrapUpDistributionItemDto[]> {
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<WrapUpDistributionItemDto[]>(resolveUrl(`${DASHBOARD}/wrap-up-distribution`), { params })
      .pipe(catchError(error => this.handleError('getWrapUpDistribution', error)));
  }

  getMetricDailyReport(metricId: number): Observable<MetricAiHumanTimeSeriesDto> {
    // metricId is a path segment, not a query parameter: ABP's conventional route for
    // DashboardAppService.GetMetricDailyReportAsync is .../metric-daily-report/{metricId}.
    // Sending it as ?metricId= matches no route at all and returns 404. The generated
    // proxy agrees — see src/app/proxy/dashboard.service.ts getMetricDailyReport.
    return this.http
      .get<MetricAiHumanTimeSeriesDto>(resolveUrl(`${DASHBOARD}/metric-daily-report/${metricId}`))
      .pipe(catchError(error => this.handleError('getMetricDailyReport', error)));
  }

  getCsatValue(request: BaseRequestDto): Observable<CsatPercentageResultDto> {
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<CsatPercentageResultDto>(resolveUrl(`${DASHBOARD}/csat-value`), { params })
      .pipe(catchError(error => this.handleError('getCsatValue', error)));
  }

  getEstimatedCsatValue(request: BaseRequestDto): Observable<CsatPercentageResultDto> {
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<CsatPercentageResultDto>(resolveUrl(`${DASHBOARD}/estimated-csat-value`), {
        params,
      })
      .pipe(catchError(error => this.handleError('getEstimatedCsatValue', error)));
  }

  getAiAgentCount(request: BaseRequestDto): Observable<number> {
    return this.getSessionCount('ai-agent-count', request);
  }

  getAiAgentChart(): Observable<ChartDataPointDto[]> {
    return this.getChart('get-ai-agent-chart');
  }

  getHumanAgentCount(request: BaseRequestDto): Observable<number> {
    return this.getSessionCount('human-agent-count', request);
  }

  getConversationCount(request: BaseRequestDto): Observable<number> {
    return this.getSessionCount('conversation-count', request);
  }

  getHumanAgentChart(): Observable<ChartDataPointDto[]> {
    return this.getChart('get-human-agent-chart');
  }

  getTokenCount(): Observable<number> {
    return this.getCount('get-token-count');
  }

  getTokenChart(): Observable<ChartDataPointDto[]> {
    return this.getChart('get-token-chart');
  }

  getMetricsLastUpdated(): Observable<Record<number, MetricLastUpdatedDto>> {
    return this.http
      .get<
        Record<number, MetricLastUpdatedDto>
      >(resolveUrl(`${DASHBOARD}/metrics-last-updated`))
      .pipe(catchError(error => this.handleError('getMetricsLastUpdated', error)));
  }

  getMetricDefinitions(): Observable<MetricDefinitionDto[]> {
    return this.http
      .get<MetricDefinitionDto[]>(resolveUrl(`${DASHBOARD}/metric-definitions`))
      .pipe(catchError(error => this.handleError('getMetricDefinitions', error)));
  }

  hasActiveSync(source?: number): Observable<boolean> {
    let params = new HttpParams();
    if (source !== undefined && source !== null) {
      params = params.append('source', source.toString());
    }
    return this.http
      .get<boolean>(resolveUrl(`${DASHBOARD}/has-active-sync`), { params })
      .pipe(catchError(error => this.handleError('hasActiveSync', error)));
  }

  private getCount(endpoint: string): Observable<number> {
    return this.http
      .get<number>(resolveUrl(`${DASHBOARD}/${endpoint}`))
      .pipe(catchError(error => this.handleError(endpoint, error)));
  }

  private getChart(endpoint: string): Observable<ChartDataPointDto[]> {
    return this.http
      .get<ChartDataPointDto[]>(resolveUrl(`${DASHBOARD}/${endpoint}`))
      .pipe(catchError(error => this.handleError(endpoint, error)));
  }

  private getSessionCount(endpoint: string, request: BaseRequestDto): Observable<number> {
    let params = new HttpParams();
    if (request.filters && request.filters.length > 0) {
      params = params.append('filters', this.serializeFilters(request));
    }
    if (request.timeResolution) {
      params = params.append('timeResolution', request.timeResolution);
    }
    return this.http
      .get<number>(resolveUrl(`${DASHBOARD}/${endpoint}`), { params })
      .pipe(catchError(error => this.handleError(endpoint, error)));
  }

  private handleError(context: string, error: unknown): Observable<never> {
    console.error(`DashboardService.${context} error:`, error);
    return throwError(() => error);
  }

  private serializeFilters(request: BaseRequestDto): string {
    return JSON.stringify(request.filters);
  }
}
